import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, Grid, Card, CardContent, CircularProgress, Alert, Button, FormControl, InputLabel, Select, MenuItem, Chip, useMediaQuery, useTheme, IconButton, Stack } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { getAccounts } from '../services/api';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getCategoryInsights } from '../services/api';  // Add to imports

function CategoryInsightsPage() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchCategoryData();
    }
  }, [selectedAccount]);

  const fetchAccounts = async () => {
    try {
      const response = await getAccounts();
      setAccounts(response.data);
      if (response.data.length > 0) {
        setSelectedAccount(response.data[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch accounts', err);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryData = async () => {
  try {
    setLoading(true);
    const response = await getCategoryInsights(selectedAccount);
    setData(response.data);
  } catch (err) {
    console.error('Failed to fetch category insights', err);
    setError('Failed to load category insights');
  } finally {
    setLoading(false);
  }
};

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload[0]) {
      const data = payload[0].payload;
      
      // Mobile: Simplified, compact tooltip
      if (isMobile) {
        return (
          <Box sx={{ 
            bgcolor: 'background.paper', 
            p: 1.5, 
            border: 1, 
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 3,
            maxWidth: 200
          }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {label}
            </Typography>
            <Typography 
              variant="body2" 
              color={data.this_month_activity >= 0 ? 'success.main' : 'error.main'}
              fontWeight="medium"
            >
              {data.this_month_activity >= 0 ? '+' : ''}{formatCurrency(data.this_month_activity)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {data.activity_change_percentage > 0 ? '+' : ''}{data.activity_change_percentage}% vs last month
            </Typography>
          </Box>
        );
      }
      
      // Desktop: Keep existing detailed tooltip
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 2, border: 1, borderColor: 'divider' }}>
          <Typography variant="body1" fontWeight="bold">
            {label}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Start of Month: {formatCurrency(data.start_balance)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Current Balance: {formatCurrency(data.current_balance)}
          </Typography>
          <Typography variant="body2" color={data.this_month_activity >= 0 ? 'success.main' : 'error.main'}>
            This Month Activity: {formatCurrency(data.this_month_activity)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Last Month Activity: {formatCurrency(data.last_month_activity)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const getBarColor = (activity) => {
    if (activity > 0) return '#4CAF50'; // Green for positive activity (income)
    if (activity < 0) return '#F44336'; // Red for negative activity (expenses)
    return '#9E9E9E'; // Gray for no activity
  };

// Enhanced mobile list view component
const MobileCategoryList = ({ data }) => {
  const getChangeIcon = (percentage) => {
    if (percentage > 0) return '↑';
    if (percentage < 0) return '↓';
    return '→';
  };
  
  const getChangeColor = (activity) => {
    if (activity > 0) return 'success.main';
    if (activity < 0) return 'error.main';
    return 'text.secondary';
  };
  
  return (
    <Card sx={{ p: { xs: 2, md: 3 } }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {data.account_name} - Category Changes (This Month vs Last Month)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          🟢 Green = Increase | 🔴 Red = Decrease | ⚪ Gray = No Change
        </Typography>
        
        <Stack spacing={2}>
          {data.category_comparison.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderRadius: 1,
                bgcolor: item.this_month_activity > 0 ? 'success.light' : 
                        item.this_month_activity < 0 ? 'error.light' : 
                        'grey.100',
                border: 1,
                borderColor: item.this_month_activity > 0 ? 'success.main' : 
                            item.this_month_activity < 0 ? 'error.main' : 
                            'grey.300'
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  {item.category}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatCurrency(item.start_balance)} → {formatCurrency(item.current_balance)}
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="h6"
                  color={getChangeColor(item.this_month_activity)}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    fontWeight: 'bold'
                  }}
                >
                  {item.this_month_activity > 0 && '+'}
                  {formatCurrency(item.this_month_activity)}
                  <Typography component="span" variant="h6">
                    {getChangeIcon(item.activity_change_percentage)}
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.activity_change_percentage > 0 && '+'}
                  {item.activity_change_percentage}% vs last
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Category Insights - Month-over-Month Comparison
        </Typography>

        {/* Navigation */}
        <Box sx={{ mb: 3 }}>
          <Button 
            variant="outlined"
            component={Link} 
            to="/analytics"
            sx={{ mr: 1 }}
          >
            Overview
          </Button>
          <Button 
            variant="contained"
            sx={{ mr: 1 }}
          >
            Category Insights
          </Button>
          <Button 
            variant="outlined"
            component={Link} 
            to="/analytics/accounts"
          >
            Account Performance
          </Button>
        </Box>

        {/* Account Selector */}
        <FormControl sx={{ mb: 3, minWidth: 200 }}>
          <InputLabel>Select Account</InputLabel>
          <Select
            value={selectedAccount}
            label="Select Account"
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {data && (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Trend Alerts */}
            {data.trend_alerts.length > 0 && (
              <Grid item xs={12}>
                <Card sx={{ p: { xs: 2, md: 3 } }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      ⚠️ Income Optimization Opportunities
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {data.trend_alerts.map((alert, index) => (
                        <Chip
                          key={index}
                          icon={<TrendingDownIcon />}
                          label={alert.message}
                          color="warning"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Category Changes - List for mobile, Chart for desktop */}
<Grid item xs={12}>
  {isMobile ? (
    // Mobile: Enhanced list view
    <MobileCategoryList data={data} />
  ) : (
    // Desktop: Keep the bar chart
    <Card sx={{ p: { xs: 2, md: 3 } }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {data.account_name} - Category Changes (This Month vs Last Month)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          🟢 Green = Increase | 🔴 Red = Decrease | ⚪ Gray = No Change
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data.category_comparison} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="category" 
              angle={-45}
              textAnchor="end"
              height={100}
            />
            <YAxis tickFormatter={(value) => formatCurrency(value)} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="this_month_activity" name="This Month Activity">
              {data.category_comparison.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.this_month_activity)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )}
</Grid>

            {/* Category Details Table */}
            <Grid item xs={12}>
              <Card sx={{ p: { xs: 2, md: 3 } }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Detailed Breakdown
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    {data.category_comparison.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 2,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {item.category}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Start: {formatCurrency(item.start_balance)} → Current: {formatCurrency(item.current_balance)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Last Month Activity: {formatCurrency(item.last_month_activity)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="h6"
                            color={item.this_month_activity >= 0 ? 'success.main' : 'error.main'}
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            {item.this_month_activity >= 0 ? <TrendingUpIcon sx={{ mr: 0.5 }} /> : <TrendingDownIcon sx={{ mr: 0.5 }} />}
                            {formatCurrency(Math.abs(item.this_month_activity))}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            vs last month: {item.activity_change_percentage > 0 ? '+' : ''}{item.activity_change_percentage}%
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
    </Container>
  );
}

export default CategoryInsightsPage;